/**
 * Date utility functions for validating working days and holidays
 */

/**
 * Converts a Date to local ISO date string (YYYY-MM-DD)
 */
export const toLocalISODate = (date: Date): string => {
    const y = date.getFullYear();
    const m = (date.getMonth() + 1).toString().padStart(2, "0");
    const d = date.getDate().toString().padStart(2, "0");
    return `${y}-${m}-${d}`;
};

/**
 * Checks if a date is a weekend (Saturday or Sunday)
 */
export const isWeekend = (date: Date): boolean => {
    const sunday = 0;
    const saturday = 6;
    const dayOfWeek = date.getDay();
    
    return dayOfWeek === sunday || dayOfWeek === saturday;
};

/**
 * Checks if a date is a bank holiday
 */
export const isBankHoliday = (date: Date, bankHolidays?: string[]): boolean => {
    if (!bankHolidays || bankHolidays.length === 0) {
        return false;
    }

    const isoDate = toLocalISODate(date);
    
    return bankHolidays.includes(isoDate);
};

/**
 * Checks if a date is a holiday (weekend or bank holiday)
 */
export const isHoliday = (date: Date, bankHolidays?: string[]): boolean => {
    return isWeekend(date) || isBankHoliday(date, bankHolidays);
};

/**
 * Checks if a date is the first working day of the week
 * Takes into account weekends and bank holidays
 */
export const isFirstWorkingDayOfWeek = (date: Date, bankHolidays?: string[]): boolean => {
    if (isHoliday(date, bankHolidays)) {
        return false;
    }

    // 0=Sun, 1=Mon, 2=Tues, 3=Wed, 4=Thurs, 5=Fri, 6=Sat in JavaScript
    const dayOfWeek = date.getDay();
    const monday = 1;
    const weekDaysCountBeforeThisDay = dayOfWeek - monday;
   
    for (let i = 1; i <= weekDaysCountBeforeThisDay; i++) {
        const previousDay = new Date(date);
        previousDay.setDate(date.getDate() - i);
        if (!isHoliday(previousDay, bankHolidays)) {
            return false;
        }   
    }

    return true;
};

/**
 * Checks if a date is the last working day of the week
 * Takes into account weekends and bank holidays
 */
export const isLastWorkingDayOfWeek = (date: Date, bankHolidays?: string[]): boolean => {
    if (isHoliday(date, bankHolidays)) {
        return false;
    }

    // 0=Sun, 1=Mon, 2=Tues, 3=Wed, 4=Thurs, 5=Fri, 6=Sat in JavaScript
    const dayOfWeek = date.getDay(); 
    const friday = 5;
    const weekDaysCountAfterThisDay = friday - dayOfWeek;

    for (let i = 1; i <= weekDaysCountAfterThisDay; i++) {
        const nextDay = new Date(date);
        nextDay.setDate(date.getDate() + i);
        if (!isHoliday(nextDay, bankHolidays)) {
            return false; // Found a non-holiday after this date, so it's not the last working day
        }   
    }
    
    return true;
};

/**
 * Checks if a date is a valid handover date (first or last working day of the week)
 * This is the main function to use for filtering dates in both DateField and CheckAvailabilityPage
 */
export const isValidHandoverDate = (date: Date, bankHolidays?: string[]): boolean => {
    return isFirstWorkingDayOfWeek(date, bankHolidays) || isLastWorkingDayOfWeek(date, bankHolidays);
};
